import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import ShareModal from "@/components/feature/ShareModal";
import StateBlock from "@/components/feature/StateBlock";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { formatViews, getCategoryByName, getNewsById, getRelatedNews } from "@/lib/news";
import RelatedNews from "./components/RelatedNews";

export default function DetailPage() {
  const { newsId } = useParams();
  const navigate = useNavigate();
  const news = newsId ? getNewsById(newsId) : undefined;
  const [liked, setLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(news?.likes ?? 0);
  const [shareOpen, setShareOpen] = useState(false);

  useDocumentMeta({
    title: news ? `${news.title}_91爆料详细报道` : "新闻详情_91爆料",
    description: news ? `${news.summary}（91爆料独家报道，关键词：${news.tags.join("、")}）` : "91爆料新闻详情页。",
    keywords: news ? `91爆料,${news.tags.join(",")},${news.category}新闻` : "91爆料,新闻详情",
  });

  useEffect(() => {
    setLiked(false);
    setLikeCount(news?.likes ?? 0);
  }, [newsId, news]);

  if (!news) {
    return (
      <div className="mx-auto max-w-[1280px] px-4 py-16 md:px-6">
        <StateBlock
          icon="ri-file-warning-line"
          title="新闻内容不存在"
          description="这条新闻可能已被删除或链接有误，你可以返回首页继续浏览其他热点爆料。"
          actionLabel="返回首页"
          onAction={() => navigate("/")}
        />
      </div>
    );
  }

  const cat = getCategoryByName(news.category);

  const handleLike = () => {
    setLiked((prev) => {
      setLikeCount((c) => (prev ? c - 1 : c + 1));
      return !prev;
    });
  };

  return (
    <div className="mx-auto max-w-[1280px] px-4 py-8 md:px-6">
      {/* 面包屑 */}
      <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
        <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
          首页
        </Link>
        <i className="ri-arrow-right-s-line"></i>
        {cat && (
          <>
            <Link to={`/category/${cat.key}`} className="cursor-pointer transition-colors hover:text-primary-600">
              {cat.name}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
          </>
        )}
        <span className="line-clamp-1 max-w-[60%] text-foreground-800">正文</span>
      </nav>

      <article className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5 md:p-8">
        {/* 标题与元信息 */}
        <header>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 text-xs font-medium text-background-50">
              {news.category}
            </span>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 text-xs text-secondary-700">
              {news.subCategory}
            </span>
            {news.tags.map((t) => (
              <span key={t} className="rounded-md bg-accent-50 px-2.5 py-1 text-xs text-accent-700">
                #{t}
              </span>
            ))}
          </div>
          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-foreground-900 md:text-4xl">
            {news.title}
          </h1>
          {news.subtitle && (
            <p className="mt-3 border-l-2 border-accent-500 pl-3 text-base text-foreground-600">{news.subtitle}</p>
          )}
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-y border-background-200 py-3 text-sm text-foreground-500">
            <span className="flex items-center gap-1.5">
              <i className="ri-user-3-line text-primary-600"></i>
              {news.author}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-calendar-line text-primary-600"></i>
              {news.publishDate}
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-eye-line text-primary-600"></i>
              {formatViews(news.views)} 阅读
            </span>
            <span className="flex items-center gap-1.5">
              <i className="ri-message-3-line text-primary-600"></i>
              评论演示
            </span>
          </div>
        </header>

        {/* 封面大图 */}
        <figure className="mt-6">
          <div className="h-[240px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[420px]">
            <img
              src={news.cover}
              alt={`${news.title} - 91爆料报道封面图`}
              title={`${news.title} - 91爆料`}
              className="h-full w-full object-cover"
            />
          </div>
          <figcaption className="mt-2 text-center text-xs text-foreground-400">{news.title} · 图片来源：91爆料</figcaption>
        </figure>

        {/* 正文 */}
        <div className="mt-8 max-w-3xl">
          {news.content.map((block, i) => {
            if (block.type === "h2") {
              return (
                <h2 key={i} className="mb-3 mt-8 font-heading text-xl font-bold text-foreground-900 md:text-2xl">
                  {block.text}
                </h2>
              );
            }
            if (block.type === "quote") {
              return (
                <blockquote
                  key={i}
                  className="my-6 rounded-r-lg border-l-4 border-accent-500 bg-accent-50 px-5 py-4 text-base italic leading-relaxed text-foreground-700"
                >
                  {block.text}
                </blockquote>
              );
            }
            if (block.type === "img") {
              return (
                <figure key={i} className="my-6">
                  <div className="h-[220px] w-full overflow-hidden rounded-lg bg-background-200 md:h-[340px]">
                    <img
                      src={block.src}
                      alt={block.caption || news.title}
                      title={`${news.title} - 91爆料`}
                      className="h-full w-full object-cover"
                    />
                  </div>
                  {block.caption && (
                    <figcaption className="mt-2 text-center text-xs text-foreground-400">{block.caption}</figcaption>
                  )}
                </figure>
              );
            }
            return (
              <p key={i} className="mb-4 text-base leading-loose text-foreground-700 md:text-[17px]">
                {block.text}
              </p>
            );
          })}
        </div>

        {/* 操作栏 */}
        <div className="mt-10 flex flex-wrap items-center justify-between gap-4 border-t border-background-200 pt-6">
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleLike}
              className={`flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                liked ? "bg-accent-500 text-background-50" : "bg-background-100 text-foreground-700 hover:bg-background-200"
              }`}
            >
              <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
              {formatViews(likeCount)} 点赞
            </button>
            <button
              type="button"
              onClick={() => setShareOpen(true)}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-share-forward-line"></i>
              分享这条爆料
            </button>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full border border-background-300 px-4 py-2 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
            >
              <i className="ri-arrow-left-line"></i>
              返回上一页
            </button>
            {cat && (
              <Link
                to={`/category/${cat.key}`}
                className="flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-secondary-100 px-4 py-2 text-sm text-secondary-800 transition-colors hover:bg-secondary-200"
              >
                更多{cat.name}新闻
                <i className="ri-arrow-right-line"></i>
              </Link>
            )}
          </div>
        </div>
      </article>

      <RelatedNews items={getRelatedNews(news, 4)} />

      <ShareModal open={shareOpen} onClose={() => setShareOpen(false)} title={news.title} url={window.location.href} />
    </div>
  );
}